/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Space, DatePicker, Typography, Tooltip, Breadcrumb, Checkbox, Spin, Modal, Tabs, Switch } from 'antd';
import { FilePdfOutlined, SettingOutlined, DashboardOutlined, ClockCircleOutlined, CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axiosConfig from '../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { loadDprSkipStatesForSection, updateDprSavedState, updateDprSkipState } from '../redux/dpr/action';
import SafetyLog from './DPR/SafetyLog';
import QualityLog from './DPR/QualityLog';
import ProductivityLog from './DPR/ProductivityLog';
import PersonnelLog from './DPR/PersonnelLog';
import ConsumablesLog from './DPR/ConsumablesLog';
import DowntimeLog from './DPR/DowntimeLog';
import CommentsLog from './DPR/CommentsLog';
import DashboardLog from './DPR/DashboardLog';
import { displayExpectedPipeLength, formatExpectedLength, sumProjectExpectedLength } from '../utils/sowExpectedLength';
import { formatPlannedWorkSummary, displayPlannedQtySummary } from '../utils/plannedWorkItems';
import { ProjectTabTableEmpty } from './project/ProjectTabTableEmpty';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const DPR = (props) => {
  const dispatch = useDispatch();
  const {
    selectedProject,
    projectTeamMembersAccessList,
    userAccessList,
  } = props;

  const [selectedRange, setSelectedRange] = useState(null);
  const [currentStep, setCurrentStep] = useState('list'); // 'list', 'pipelineSelection', 'form', or 'settings'
  const [activeCategory, setActiveCategory] = useState('Productivity');
  const [activeShift, setActiveShift] = useState('day');
  const [skipped, setSkipped] = useState({ day: false, night: false, all: false });
  const [selectedDate, setSelectedDate] = useState(moment());
  const [activeSettingsTab, setActiveSettingsTab] = useState('reportConfig');
  const [reportConfig, setReportConfig] = useState({
    summary: true,
    safety: true,
    quality: true,
    productivity: true,
    personnel: true,
    comments: true,
    downtime: true,
    consumables: true,
    generateReportAfterSubmission: false
  });
  const [defectCodes, setDefectCodes] = useState([
    { id: 1, code: '', description: '' }
  ]);
  const [pipelineSections, setPipelineSections] = useState([]);
  const pipelineProjectExpectedLength = useMemo(
    () => sumProjectExpectedLength(pipelineSections),
    [pipelineSections]
  );
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);
  const [isLoadingDprForm, setIsLoadingDprForm] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [dprLogId, setDprLogId] = useState(null);
  const [dprStatus, setDprStatus] = useState(null);
  const [dprLogData, setDprLogData] = useState(null);
  const [isSavingSafety, setIsSavingSafety] = useState(false);
  const [isSavingQuality, setIsSavingQuality] = useState(false);
  const [isSavingProductivity, setIsSavingProductivity] = useState(false);
  const [isSavingPersonnel, setIsSavingPersonnel] = useState(false);
  const [isSavingConsumables, setIsSavingConsumables] = useState(false);
  const [isSavingDowntime, setIsSavingDowntime] = useState(false);
  const [isSavingComments, setIsSavingComments] = useState(false);
  const savedStatesBySection = useSelector(state => state.dprReducer?.savedStatesBySection || {});
  const skipStatesBySection = useSelector(state => state.dprReducer?.skipStatesBySection || {});

  const markSectionSaved = (sectionKey) => {
    if (selectedPipeline?.id) {
      dispatch(updateDprSavedState(selectedPipeline.id, sectionKey, true));
    }
  };

  const categoriesWithProgressIcon = new Set([
    'Safety',
    'Productivity',
    'Quality', 
    'Personnel',
    'Consumables',
    'Downtime'
  ]);

  const renderCategoryLabel = (category, isActive) => {
    const showIcon = categoriesWithProgressIcon.has(category);
    const progressIconColor = isActive ? '#fff' : '#ff4d4f';
    const savedColor = '#52c41a';
    const skippedColor = '#faad14';
    const currentSowId = selectedPipeline?.id;
    const savedKeyMap = {
      Safety: 'savedSafety',
      Quality: 'savedQuality',
      Productivity: 'savedProductivity',
      Personnel: 'savedPersonnel',
      Consumables: 'savedConsumables',
      Downtime: 'savedDowntime'
    };
    const savedKey = savedKeyMap[category];
    const isSaved = currentSowId && savedKey
      ? Boolean(savedStatesBySection?.[currentSowId]?.[savedKey])
      : false;
    const skipKeyMap = {
      Safety: 'skipSafety',
      Quality: 'skipQuality',
      Productivity: 'skipProductivity',
      Personnel: 'skipPersonnel',
      Consumables: 'skipConsumables',
      Downtime: 'skipDowntime'
    };
    const skipKey = skipKeyMap[category];
    const isSkipped = currentSowId && skipKey
      ? Boolean(skipStatesBySection?.[currentSowId]?.[skipKey])
      : false;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        {showIcon && (
          isSkipped ? (
            <MinusCircleOutlined
              style={{
                fontSize: '14px',
                color: skippedColor
              }}
            />
          ) : isSaved ? (
            <CheckCircleOutlined
              style={{
                fontSize: '14px',
                color: savedColor
              }}
            />
          ) : (
            <ClockCircleOutlined
              style={{
                fontSize: '14px',
                color: progressIconColor
              }}
            />
          )
        )}
        <span>{category}</span>
      </span>
    );
  };
  const [isSavingReportConfig, setIsSavingReportConfig] = useState(false);

  // Safety Log Data - will be dynamic (empty array will use default data from SafetyLog component)
  const [safetyData, setSafetyData] = useState([]);

  // Quality Log Data - will be dynamic (empty array will use default data from QualityLog component)
  const [qualityData, setQualityData] = useState([]);

  // Productivity Log Data - will be dynamic
  const [productivityData, setProductivityData] = useState({});
  /** Bump when switching to Productivity so ProductivityLog refetches (force refresh after first visit). */
  const [productivityTabRefreshToken, setProductivityTabRefreshToken] = useState(0);

  // Personnel Log Data - will be dynamic
  const [personnelData, setPersonnelData] = useState({});

  // Consumables Log Data - will be dynamic
  const [consumablesData, setConsumablesData] = useState({});

  // Downtime Log Data - will be dynamic
  const [downtimeData, setDowntimeData] = useState({});

  // Comments Log Data - will be dynamic
  const [commentsData, setCommentsData] = useState({});

  // Dummy data for now
  const dummyData = [
    {
      id: 1,
      logDate: moment().subtract(2, 'days').format('YYYY-MM-DD'),
      userFirstName: 'John',
      userLastName: 'Doe',
      reportDetails: {
        fileUrl: '#'
      }
    },
    {
      id: 2,
      logDate: moment().subtract(1, 'days').format('YYYY-MM-DD'),
      userFirstName: 'Jane',
      userLastName: 'Smith',
      reportDetails: null
    },
    {
      id: 3,
      logDate: moment().format('YYYY-MM-DD'),
      userFirstName: 'Bob',
      userLastName: 'Johnson',
      reportDetails: {
        fileUrl: '#'
      }
    },
  ];

  const [dprLogs, setDprLogs] = useState([]);
  const [dprLogsPagination, setDprLogsPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0
  });
  const [isLoadingDprLogs, setIsLoadingDprLogs] = useState(false);

  // Fetch Report Config
  const fetchReportConfig = async () => {
    if (!selectedProject?.id) return;

    try {
      const response = await axiosConfig.get(`/dpr-report-config?projectId=${selectedProject.id}`);
      if (response.data && response.data.data) {
        const config = response.data.data;
        setReportConfig({
          summary: config.includeSummary === 1,
          safety: config.includeSafety === 1,
          quality: config.includeQuality === 1,
          productivity: config.includeProductivity === 1,
          personnel: config.includePersonnel === 1,
          comments: config.includeComments === 1,
          downtime: config.includeDowntime === 1,
          consumables: config.includeConsumables === 1,
          generateReportAfterSubmission: config.generateReportAfterSubmission === 1
        });
      }
    } catch (error) {
      console.error('Error fetching report config:', error);
    }
  };

  // Save Report Config
  const handleSaveReportConfig = async () => {
    if (!selectedProject?.id) {
      console.error('Project ID is required');
      return;
    }

    setIsSavingReportConfig(true);
    try {
      const payload = {
        projectId: selectedProject.id,
        includeSummary: reportConfig.summary ? 1 : 0,
        includeSafety: reportConfig.safety ? 1 : 0,
        includeQuality: reportConfig.quality ? 1 : 0,
        includeProductivity: reportConfig.productivity ? 1 : 0,
        includePersonnel: reportConfig.personnel ? 1 : 0,
        includeComments: reportConfig.comments ? 1 : 0,
        includeDowntime: reportConfig.downtime ? 1 : 0,
        includeConsumables: reportConfig.consumables ? 1 : 0,
        generateReportAfterSubmission: reportConfig.generateReportAfterSubmission ? 1 : 0
      };

      await axiosConfig.put(`/dpr-report-config?projectId=${selectedProject.id}`, payload);
      console.log('Report config saved successfully');
      // Optionally show a success message
    } catch (error) {
      console.error('Error saving report config:', error);
      // Optionally show an error message
    } finally {
      setIsSavingReportConfig(false);
    }
  };

  // Fetch Welder Config (Defect Codes)
  const fetchWelderConfig = async () => {
    if (!selectedProject?.id) return;

    try {
      const response = await axiosConfig.get(`/dpr-welder-config?projectId=${selectedProject.id}`);
      if (response.data && response.data.data) {
        const configs = response.data.data.map(config => ({
          id: config.id,
          code: config.code || '',
          description: config.description || '',
          isExisting: true // Flag to track existing entries
        }));
        setDefectCodes(configs.length > 0 ? configs : [{ id: Date.now(), code: '', description: '', isExisting: false }]);
      }
    } catch (error) {
      console.error('Error fetching welder config:', error);
    }
  };

  // Save Welder Config (Defect Codes)
  const handleSaveWelderConfig = async () => {
    if (!selectedProject?.id) {
      console.error('Project ID is required');
      return;
    }

    setIsSavingReportConfig(true);
    try {
      const welderConfigs = defectCodes.map(defect => {
        const config = {
          code: defect.code || '',
          description: defect.description || ''
        };
        // Include id only for existing entries
        if (defect.isExisting && defect.id) {
          config.id = defect.id;
        }
        return config;
      });

      const payload = {
        projectId: selectedProject.id,
        welderConfigs
      };

      await axiosConfig.put(`/dpr-welder-config`, payload);
      console.log('Welder config saved successfully');
      // Refresh the data after save
      await fetchWelderConfig();
    } catch (error) {
      console.error('Error saving welder config:', error);
    } finally {
      setIsSavingReportConfig(false);
    }
  };

  // Fetch DPR Logs
  const fetchDprLogs = async (limit = 20, offset = 0, dateRange = null) => {
    if (!selectedProject?.id) return;

    setIsLoadingDprLogs(true);
    try {
      const projectId = selectedProject.id;

      // Build query parameters
      const params = new URLSearchParams({
        projectId: projectId.toString(),
        limit: limit.toString(),
        offset: offset.toString()
      });

      // Use provided dateRange or fall back to selectedRange state
      const rangeToUse = dateRange !== null ? dateRange : selectedRange;

      // Add date range if selected
      if (rangeToUse && rangeToUse.length === 2 && rangeToUse[0] && rangeToUse[1]) {
        const fromDate = rangeToUse[0].format('YYYY-MM-DD');
        const toDate = rangeToUse[1].format('YYYY-MM-DD');
        params.append('fromDate', fromDate);
        params.append('toDate', toDate);
      }

      const response = await axiosConfig.get(`/dpr-logs-paged?${params.toString()}`);

      if (response.data && response.data.data) {
        const { items, total } = response.data.data;
        setDprLogs(items || []);
        setDprLogsPagination(prev => ({
          ...prev,
          limit,
          offset,
          total: total || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching DPR logs:', error);
      setDprLogs([]);
    } finally {
      setIsLoadingDprLogs(false);
    }
  };

  // Fetch report config when settings tab is opened
  useEffect(() => {
    if (activeSettingsTab === 'reportConfig' && currentStep === 'settings') {
      fetchReportConfig();
    }
    if (activeSettingsTab === 'defectTypes' && currentStep === 'settings') {
      fetchWelderConfig();
    }
  }, [activeSettingsTab, currentStep, selectedProject]);

  // Fetch DPR logs when component mounts or when currentStep is 'list'
  useEffect(() => {
    if (currentStep === 'list' && selectedProject?.id) {
      // Reset to first page when navigating to list
      fetchDprLogs(20, 0);
    }
  }, [currentStep, selectedProject]);

  // Reset activeCategory to 'Productivity' when navigating to form
  useEffect(() => {
    if (currentStep === 'form') {
      setActiveCategory('Productivity');
    }
  }, [currentStep]);

  const handleCustomDateSelect = (dates, dateStrings) => {
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      // Both dates are selected - apply filter
      setSelectedRange(dates);
      // Pass the dates directly to fetchDprLogs to avoid state update delay
      fetchDprLogs(20, 0, dates);
    } else {
      // Date range is cleared - remove filter
      setSelectedRange(null);
      // Pass null to explicitly remove date filter
      fetchDprLogs(20, 0, null);
    }
  };

  // Handle pagination change
  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    const newOffset = (current - 1) * pageSize;
    fetchDprLogs(pageSize, newOffset);
  };

  const handleAddDpr = () => {
    setCurrentStep('pipelineSelection');
    fetchPipelineSections();
  };

  const fetchPipelineSections = async () => {
    setIsLoadingPipelines(true);
    try {
      const projectId = selectedProject?.id || 1; // Use selectedProject id or default to 1
      const response = await axiosConfig.get(`/sows?projectId=${projectId}`);

      console.log('Pipeline Sections API Response:', response);

      if (response.data && Array.isArray(response.data)) {
        setPipelineSections(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setPipelineSections(response.data.data);
      } else {
        setPipelineSections([]);
      }
    } catch (error) {
      console.error('Error fetching pipeline sections:', error);
      setPipelineSections([]);
    } finally {
      setIsLoadingPipelines(false);
    }
  };

  const handlePipelineSelect = async (pipeline) => {
    setIsLoadingDprForm(true);
    try {
      // Get current date in YYYY-MM-DD format
      const logDate = moment(selectedDate).format('YYYY-MM-DD');

      // Get sowId from pipeline id
      const sowId = pipeline?.id;

      // Fetch DPR log data
      await fetchDprLogData(logDate, sowId);

      // Set selected pipeline and navigate to form
      setSelectedPipeline(pipeline);

      // Load skip states for this pipeline section
      dispatch(loadDprSkipStatesForSection(sowId));

      setCurrentStep('form');
    } catch (error) {
      console.error('Error fetching DPR log:', error);
      // Still proceed to form even if API call fails
      setSelectedPipeline(pipeline);
      setCurrentStep('form');
    } finally {
      setIsLoadingDprForm(false);
    }
  };

  const handleSkipChange = (type, checked) => {
    if (type === 'all') {
      setSkipped({ day: checked, night: checked, all: checked });
    } else {
      const newSkipped = { ...skipped, [type]: checked };
      if (checked && newSkipped.day && newSkipped.night) {
        newSkipped.all = true;
      } else {
        newSkipped.all = false;
      }
      setSkipped(newSkipped);
    }
  };

  // Reusable function to fetch DPR log data
  const fetchDprLogData = async (logDate, sowId) => {
    try {
      // Get user email from localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      const logCreatedBy = user?.email || '';

      // Get projectId from selectedProject
      const projectId = selectedProject?.id || 1;

      // Build query parameters
      const params = new URLSearchParams({
        projectId: projectId.toString(),
        logCreatedBy: logCreatedBy,
        logDate: logDate,
        sowId: sowId.toString()
      });

      // Call the GET endpoint
      const response = await axiosConfig.get(`/dpr-log?${params.toString()}`);

      console.log('DPR Log API Response:', response);

      // Extract and store the dprLogId and status from the response
      const responseData = response?.data?.data || response?.data;
      setDprLogData(responseData || null);
      if (responseData?.id) {
        setDprLogId(responseData.id);
      }
      if (responseData?.status) {
        setDprStatus(responseData.status);
      }

      // Update saved state indicators based on submitted flags from API
      if (sowId) {
        const isDay = activeShift === 'day';
        const savedFlags = {
          savedSafety: Boolean(isDay ? responseData?.isSafetySubmittedDayShift : responseData?.isSafetySubmittedNightShift),
          savedQuality: Boolean(isDay ? responseData?.isQualitySubmittedDayShift : responseData?.isQualitySubmittedNightShift),
          savedProductivity: Boolean(isDay ? responseData?.isProductivitySubmittedDayShift : responseData?.isProductivitySubmittedNightShift),
          savedPersonnel: Boolean(isDay ? responseData?.isPersonnelSubmittedDayShift : responseData?.isPersonnelSubmittedNightShift),
          savedConsumables: Boolean(isDay ? responseData?.isConsumablesSubmittedDayShift : responseData?.isConsumablesSubmittedNightShift),
          savedDowntime: Boolean(isDay ? responseData?.isDowntimeSubmittedDayShift : responseData?.isDowntimeSubmittedNightShift)
        };

        Object.entries(savedFlags).forEach(([key, value]) => {
          dispatch(updateDprSavedState(sowId, key, value));
        });

        const skipFlags = {
          skipSafety: Boolean(isDay ? responseData?.isSafetySkippedDayShift : responseData?.isSafetySkippedNightShift),
          skipQuality: Boolean(isDay ? responseData?.isQualitySkippedDayShift : responseData?.isQualitySkippedNightShift),
          skipProductivity: Boolean(isDay ? responseData?.isProductivitySkippedDayShift : responseData?.isProductivitySkippedNightShift),
          skipPersonnel: Boolean(isDay ? responseData?.isPersonnelSkippedDayShift : responseData?.isPersonnelSkippedNightShift),
          skipConsumables: Boolean(isDay ? responseData?.isConsumablesSkippedDayShift : responseData?.isConsumablesSkippedNightShift),
          skipDowntime: Boolean(isDay ? responseData?.isDowntimeSkippedDayShift : responseData?.isDowntimeSkippedNightShift)
        };

        Object.entries(skipFlags).forEach(([key, value]) => {
          dispatch(updateDprSkipState(sowId, key, value));
        });
      }

      // Prepopulate comments data if approvedBy1/approvedBy2 exist or if status is submitted
      // Check if there's any approval data (signatures or names)
      const hasApprovalData = (responseData.approvedBy1 && (responseData.approvedBy1.teamMemberName || responseData.approvedBy1.signature)) ||
        (responseData.approvedBy2 && (responseData.approvedBy2.teamMemberName || responseData.approvedBy2.signature)) ||
        responseData.managerSignatureS3Object ||
        responseData.clientSignatureS3Object;

      // Always set comments data if there's approval data or if status is submitted
      const commentsDataToSet = {
        dprDayShiftComments: responseData.dprDayShiftComments || '',
        dprNightShiftComments: responseData.dprNightShiftComments || '',
        managerName: responseData.approvedBy1?.teamMemberName || '',
        clientName: responseData.approvedBy2?.teamMemberName || '',
        managerSignature: responseData.approvedBy1?.signature || responseData.managerSignatureS3Object?.objectUrl || '',
        clientSignature: responseData.approvedBy2?.signature || responseData.clientSignatureS3Object?.objectUrl || '',
        managerSignatureTimestamp: responseData.approvedBy1?.signedAt || null,
        clientSignatureTimestamp: responseData.approvedBy2?.signedAt || null
      };

      const hasComments =
        (responseData?.dprDayShiftComments && responseData.dprDayShiftComments.trim() !== '') ||
        (responseData?.dprNightShiftComments && responseData.dprNightShiftComments.trim() !== '');

      if (responseData?.status === 'submitted' || hasApprovalData || hasComments) {
        setCommentsData(commentsDataToSet);
      } else {
        // Reset comments data if no approval data, no comments, and not submitted
        setCommentsData({});
      }

      return responseData;
    } catch (error) {
      console.error('Error fetching DPR log:', error);
      // Reset states on error
      setDprLogId(null);
      setDprStatus(null);
      setCommentsData({});
      throw error;
    }
  };

  const handleDateChange = async (date) => {
    setSelectedDate(date);

    // Only fetch if we're in form view and have a selected pipeline
    if (currentStep === 'form' && selectedPipeline?.id && date) {
      setIsLoadingDprForm(true);
      try {
        const logDate = moment(date).format('YYYY-MM-DD');
        await fetchDprLogData(logDate, selectedPipeline.id);
      } catch (error) {
        console.error('Error fetching DPR log for selected date:', error);
        // Optionally show error message to user
      } finally {
        setIsLoadingDprForm(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedPipeline?.id || !dprLogData) return;
    const isDay = activeShift === 'day';
    const skipFlags = {
      skipSafety: Boolean(isDay ? dprLogData?.isSafetySkippedDayShift : dprLogData?.isSafetySkippedNightShift),
      skipQuality: Boolean(isDay ? dprLogData?.isQualitySkippedDayShift : dprLogData?.isQualitySkippedNightShift),
      skipProductivity: Boolean(isDay ? dprLogData?.isProductivitySkippedDayShift : dprLogData?.isProductivitySkippedNightShift),
      skipPersonnel: Boolean(isDay ? dprLogData?.isPersonnelSkippedDayShift : dprLogData?.isPersonnelSkippedNightShift),
      skipConsumables: Boolean(isDay ? dprLogData?.isConsumablesSkippedDayShift : dprLogData?.isConsumablesSkippedNightShift),
      skipDowntime: Boolean(isDay ? dprLogData?.isDowntimeSkippedDayShift : dprLogData?.isDowntimeSkippedNightShift)
    };

    Object.entries(skipFlags).forEach(([key, value]) => {
      dispatch(updateDprSkipState(selectedPipeline.id, key, value));
    });

    const savedFlags = {
      savedSafety: Boolean(isDay ? dprLogData?.isSafetySubmittedDayShift : dprLogData?.isSafetySubmittedNightShift),
      savedQuality: Boolean(isDay ? dprLogData?.isQualitySubmittedDayShift : dprLogData?.isQualitySubmittedNightShift),
      savedProductivity: Boolean(isDay ? dprLogData?.isProductivitySubmittedDayShift : dprLogData?.isProductivitySubmittedNightShift),
      savedPersonnel: Boolean(isDay ? dprLogData?.isPersonnelSubmittedDayShift : dprLogData?.isPersonnelSubmittedNightShift),
      savedConsumables: Boolean(isDay ? dprLogData?.isConsumablesSubmittedDayShift : dprLogData?.isConsumablesSubmittedNightShift),
      savedDowntime: Boolean(isDay ? dprLogData?.isDowntimeSubmittedDayShift : dprLogData?.isDowntimeSubmittedNightShift)
    };

    Object.entries(savedFlags).forEach(([key, value]) => {
      dispatch(updateDprSavedState(selectedPipeline.id, key, value));
    });
  }, [activeShift, dprLogData, selectedPipeline?.id, dispatch]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'logDate',
      key: 'date',
      render: (text, record) => {
        return (
          <div style={{ cursor: 'pointer' }}>
            {text ? moment(text).format('DD MMM YYYY') : '-'}
          </div>
        );
      },
      align: 'center',
    },
    {
      title: 'Created By',
      key: 'createdBy',
      render: (text, record) => {
        const firstName = record?.createdByUser?.userFirstName || '';
        const lastName = record?.createdByUser?.userLastName || '';
        const fullName = `${firstName} ${lastName}`.trim() || '-';
        return (
          <div style={{ cursor: 'pointer' }}>
            {fullName}
          </div>
        );
      },
      align: 'center'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (text) => {
        return (
          <div style={{ cursor: 'pointer' }}>
            {text ? text.charAt(0).toUpperCase() + text.slice(1) : '-'}
          </div>
        );
      },
    }
  ];

  return (
    <div className="h-100" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <style>
        {`
          .dpr-loading-modal .ant-modal-header {
            display: none !important;
          }
          .dpr-loading-modal .ant-modal-content {
            background: transparent !important;
            box-shadow: none !important;
            border: none !important;
          }
          .dpr-loading-modal .ant-modal-body {
            padding: 0 !important;
          }
          .pipeline-selection-table .ant-table {
            background-color: transparent !important;
          }
          .pipeline-selection-table .ant-table-container {
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            border-radius: 4px !important;
          }
          .pipeline-selection-table .ant-table-thead > tr > th {
            background-color: rgba(255, 255, 255, 0.1) !important;
            border-color: rgba(255, 255, 255, 0.2) !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
            color: #fff !important;
            padding: 10px 12px !important;
            font-weight: 500 !important;
            font-size: 13px !important;
            text-align: left !important;
          }
          .pipeline-selection-table .ant-table-tbody > tr > td {
            background-color: #39201d !important;
            border-color: rgba(255, 255, 255, 0.1) !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            color: #fff !important;
            padding: 10px 12px !important;
            border-radius: 0 !important;
          }
          .pipeline-selection-table .ant-table-tbody > tr.ant-table-row {
            background-color: #39201d !important;
            border-radius: 0 !important;
          }
          .pipeline-selection-table .ant-table-tbody > tr.ant-table-row:nth-child(even) {
            background-color: #39201d !important;
            border-radius: 0 !important;
          }
          .pipeline-selection-table .ant-table-tbody > tr.ant-table-row:nth-child(even) > td {
            background-color: #39201d !important;
            border-radius: 0 !important;
          }
          .pipeline-selection-table .ant-table-tbody > tr.ant-table-row:hover {
            background-color: rgba(255, 255, 255, 0.08) !important;
          }
          .pipeline-selection-table .ant-table-tbody > tr.ant-table-row:hover > td {
            background-color: rgba(255, 255, 255, 0.08) !important;
            border-radius: 0 !important;
          }
          .pipeline-selection-table .ant-table-tbody > tr:last-child > td {
            border-bottom: none !important;
            border-radius: 0 !important;
          }
          .ant-switch {
            background-color: rgba(255, 255, 255, 0.2) !important;
          }
          .ant-switch-checked {
            background-color: #ff4d4f !important;
          }
        `}
      </style>
      {currentStep !== 'list' && (
        <div style={{
          padding: '8px 24px',
          fontSize: '16px',
          fontWeight: '500',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '30px',
          margin: '8px 0',
          minWidth: '100%',
          height: '56px'
        }}>
          <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <div style={{
              padding: '2px 16px',
              height: '32px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Breadcrumb style={{ margin: 0 }}>
                <Breadcrumb.Item>
                  <a
                    onClick={() => {
                      setCurrentStep('list');
                      setSelectedPipeline(null);
                    }}
                    style={{
                      color: '#ff4d4f',
                      fontWeight: currentStep === 'list' ? 600 : 'normal',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    DPR List
                  </a>
                </Breadcrumb.Item>
                {currentStep === 'pipelineSelection' && (
                  <Breadcrumb.Item>
                    <span
                      style={{
                        color: '#ff4d4f',
                        fontWeight: 600,
                        fontSize: '14px'
                      }}
                    >
                      Select Pipeline Section
                    </span>
                  </Breadcrumb.Item>
                )}
                {selectedPipeline && currentStep === 'form' && (
                  <Breadcrumb.Item>
                    <a
                      onClick={() => setCurrentStep('pipelineSelection')}
                      style={{
                        color: '#ff4d4f',
                        fontWeight: 'normal',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {selectedPipeline.pipelineSection}
                    </a>
                  </Breadcrumb.Item>
                )}
                {currentStep === 'form' && (
                  <Breadcrumb.Item>
                    <span
                      style={{
                        color: '#ff4d4f',
                        fontWeight: 600,
                        fontSize: '14px'
                      }}
                    >
                      Add Report
                    </span>
                  </Breadcrumb.Item>
                )}
                {currentStep === 'settings' && (
                  <Breadcrumb.Item>
                    <span
                      style={{
                        color: '#ff4d4f',
                        fontWeight: 600,
                        fontSize: '14px'
                      }}
                    >
                      Settings
                    </span>
                  </Breadcrumb.Item>
                )}
              </Breadcrumb>
            </div>
          </div>
          {currentStep === 'form' && (
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              minWidth: '600px',
              justifyContent: 'flex-end',
              height: '40px'
            }}>
              {/* Skip checkboxes - before date picker */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                height: '32px',
                marginTop: '9px'
              }}>
                <Checkbox
                  className="red-checkbox"
                  style={{
                    color: '#fff',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    marginTop: '7px',
                    height: '32px',
                    margin: 0
                  }}
                  checked={skipped.night}
                  onChange={e => handleSkipChange('night', e.target.checked)}
                  disabled={skipped.all && !skipped.night}
                >
                  Skip Night Shift
                </Checkbox>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                height: '32px',
                marginTop: '9px'
              }}>
                <Checkbox
                  className="red-checkbox"
                  style={{
                    color: '#fff',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    marginTop: '7px',
                    height: '32px',
                    margin: 0
                  }}
                  checked={skipped.day}
                  onChange={e => handleSkipChange('day', e.target.checked)}
                  disabled={skipped.all && !skipped.day}
                >
                  Skip Day Shift
                </Checkbox>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                height: '32px',
                marginTop: '9px'
              }}>
                <Checkbox
                  className="red-checkbox"
                  style={{
                    color: '#fff',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    marginTop: '7px',
                    height: '32px',
                    margin: 0
                  }}
                  checked={skipped.all}
                  onChange={e => handleSkipChange('all', e.target.checked)}
                >
                  Skip DPR
                </Checkbox>
              </div>

              {/* DatePicker - in the middle */}
              <DatePicker
                placeholder="Select Date"
                style={{ minWidth: 150, height: '32px' }}
                format={(value) => (value ? value.format('DD-MMM-YYYY').toLowerCase() : '')}
                value={selectedDate}
                onChange={handleDateChange}
                disabledDate={(current) => current && current > moment().endOf('day')}
              />

              {/* Shift selection buttons - after date picker */}
              <div style={{
                display: 'flex',
                gap: '0',
                alignItems: 'center',
                height: '32px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: '2px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <button
                  onClick={() => !(skipped.night || skipped.all) && setActiveShift('night')}
                  disabled={skipped.night || skipped.all}
                  style={{
                    borderRadius: '18px',
                    height: '28px',
                    padding: '0 20px',
                    border: 'none',
                    backgroundColor: activeShift === 'night' ? '#ff4d4f' : 'transparent',
                    color: activeShift === 'night' ? '#fff' : 'rgba(255, 255, 255, 0.65)',
                    fontWeight: 500,
                    fontSize: '14px',
                    cursor: (skipped.night || skipped.all) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: (skipped.night || skipped.all) ? 0.5 : 1
                  }}
                >
                  Night
                </button>
                <button
                  onClick={() => !(skipped.day || skipped.all) && setActiveShift('day')}
                  disabled={skipped.day || skipped.all}
                  style={{
                    borderRadius: '18px',
                    height: '28px',
                    padding: '0 20px',
                    border: 'none',
                    backgroundColor: activeShift === 'day' ? '#ff4d4f' : 'transparent',
                    color: activeShift === 'day' ? '#fff' : 'rgba(255, 255, 255, 0.65)',
                    fontWeight: 500,
                    fontSize: '14px',
                    cursor: (skipped.day || skipped.all) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: (skipped.day || skipped.all) ? 0.5 : 1
                  }}
                >
                  Day
                </button>
              </div>
            </div>
          )}
          {currentStep === 'pipelineSelection' && (
            <Button
              type="text"
              icon={<SettingOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />}
              style={{
                color: '#ff4d4f',
                padding: '4px 8px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => {
                setCurrentStep('settings');
              }}
            />
          )}
        </div>
      )}
      {currentStep === 'form' && (
        <div style={{
          padding: '8px 24px',
          fontSize: '16px',
          fontWeight: '500',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '30px',
          margin: '8px 0',
          minWidth: '100%',
          height: '56px'
        }}>
          {/* Category selection buttons - third row */}
          <div style={{
            display: 'flex',
            gap: '40px',
            alignItems: 'center',
            height: '32px'
          }}>
            {['Productivity', 'Quality', 'Safety', 'Personnel', 'Consumables', 'Downtime', 'Comments'].map((category) => (
              activeCategory === category ? (
                <Button
                  key={category}
                  onClick={() => {
                    setActiveCategory(category);
                    if (category === 'Productivity') {
                      setProductivityTabRefreshToken((n) => n + 1);
                    }
                  }}
                  style={{
                    borderRadius: '6px',
                    height: '32px',
                    backgroundColor: '#ff4d4f',
                    borderColor: '#ff4d4f',
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '16px',
                    padding: '0 16px'
                  }}
                >
                  {renderCategoryLabel(category, true)}
                </Button>
              ) : (
                <span
                  key={category}
                  onClick={() => {
                    setActiveCategory(category);
                    if (category === 'Productivity') {
                      setProductivityTabRefreshToken((n) => n + 1);
                    }
                  }}
                  style={{
                    color: '#595959',
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontWeight: 400,
                    userSelect: 'none',
                    padding: '0 4px'
                  }}
                >
                  {renderCategoryLabel(category, false)}
                </span>
              )
            ))}
          </div>
          {/* Dashboard icon and label - right corner */}
          <div
            onClick={() => {
              setActiveCategory('Dashboard');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'background-color 0.3s ease',
              backgroundColor: activeCategory === 'Dashboard' ? 'rgba(255, 77, 79, 0.2)' : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (activeCategory !== 'Dashboard') {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeCategory !== 'Dashboard') {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <DashboardOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
            <span style={{
              color: activeCategory === 'Dashboard' ? '#ff4d4f' : '#fff',
              fontSize: '14px',
              fontWeight: activeCategory === 'Dashboard' ? 600 : 500
            }}>
              Dashboard
            </span>
          </div>
        </div>
      )}

      {/* Pipeline Selection & Settings: flex container so content gets remaining space and scrolls */}
      {(currentStep === 'pipelineSelection' || currentStep === 'settings') && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {currentStep === 'pipelineSelection' && (
            <div style={{ padding: '0', minHeight: '100%', position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '8px 12px 4px',
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0
              }}
              >
                <span style={{ color: '#fff', fontSize: '14px' }}>
                  <strong style={{ marginRight: 8 }}>Project expected length</strong>
                  {formatExpectedLength(pipelineProjectExpectedLength)}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px', marginLeft: 12, alignSelf: 'center' }}>
                  (planned qty × pipe length per mainline, summed)
                </span>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table
                  columns={[
                    {
                      title: 'Pipeline Section',
                      dataIndex: 'pipelineSection',
                      key: 'pipelineSection',
                      render: (text) => <span style={{ color: '#fff', fontSize: '14px' }}>{text || '-'}</span>
                    },
                    {
                      title: 'Pipe String ID',
                      dataIndex: 'pipeStringId',
                      key: 'pipeStringId',
                      render: (text) => <span style={{ color: '#fff', fontSize: '14px' }}>{text || '-'}</span>
                    },
                    {
                      title: 'Material',
                      dataIndex: 'material',
                      key: 'material',
                      render: (text) => <span style={{ color: '#fff', fontSize: '14px' }}>{text || '-'}</span>
                    },
                    {
                      title: 'Stalk #',
                      dataIndex: 'stalkId',
                      key: 'stalkId',
                      render: (text) => <span style={{ color: '#fff', fontSize: '14px' }}>{text || '-'}</span>
                    },
                    {
                      title: 'Field Joint Type',
                      dataIndex: 'fieldJointType',
                      key: 'fieldJointType',
                      render: (text) => <span style={{ color: '#fff', fontSize: '14px' }}>{text || '-'}</span>
                    },
                    {
                      title: 'Blast Standard',
                      dataIndex: 'blastStandard',
                      key: 'blastStandard',
                      render: (text) => <span style={{ color: '#fff', fontSize: '14px' }}>{text || '-'}</span>
                    },
                    {
                      title: 'Planned Works',
                      dataIndex: 'plannedWorks',
                      key: 'plannedWorks',
                      render: (_, record) => (
                        <span style={{ color: '#fff', fontSize: '14px' }}>
                          {formatPlannedWorkSummary(record)}
                        </span>
                      )
                    },
                    {
                      title: 'Planned Quantity',
                      dataIndex: 'plannedWelds',
                      key: 'plannedWelds',
                      render: (_, record) => (
                        <span style={{ color: '#fff', fontSize: '14px' }}>
                          {displayPlannedQtySummary(record)}
                        </span>
                      )
                    },
                    {
                      title: 'OD (in.)',
                      dataIndex: 'odInches',
                      key: 'odInches',
                      render: (text, record) => (
                        <span style={{ color: '#fff', fontSize: '14px' }}>
                          {text ?? record.outerDiameterInch ?? '-'}
                        </span>
                      )
                    },
                    {
                      title: 'WT (mm)',
                      dataIndex: 'wallThicknessMm',
                      key: 'wallThicknessMm',
                      render: (text) => <span style={{ color: '#fff', fontSize: '14px' }}>{text || '-'}</span>
                    },
                    {
                      title: 'Pipe length',
                      dataIndex: 'pipeLength',
                      key: 'pipeLength',
                      render: (text) => <span style={{ color: '#fff', fontSize: '14px' }}>{text || '-'}</span>
                    },
                    {
                      title: 'Expected pipe length',
                      key: 'expectedPipeLength',
                      render: (_, record) => (
                        <span style={{ color: '#fff', fontSize: '14px' }}>
                          {displayExpectedPipeLength(record)}
                        </span>
                      )
                    },
                    {
                      title: 'Pipe start date',
                      dataIndex: 'pipeStartDate',
                      key: 'pipeStartDate',
                      render: (text) => (
                        <span style={{ color: '#fff', fontSize: '14px' }}>
                          {text ? String(text).slice(0, 10) : '-'}
                        </span>
                      )
                    },
                    {
                      title: 'Action',
                      key: 'action',
                      align: 'left',
                      width: 120,
                      render: (_, record) => (
                        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <Button
                            type="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePipelineSelect(record);
                            }}
                            style={{
                              backgroundColor: '#ff4d4f',
                              borderColor: '#ff4d4f',
                              fontSize: '12px',
                              height: '28px',
                              padding: '0 16px'
                            }}
                          >
                            Select
                          </Button>
                        </div>
                      )
                    }
                  ]}
                  dataSource={pipelineSections}
                  rowKey="id"
                  pagination={false}
                  className="pipeline-selection-table"
                  onRow={(record) => ({
                    onClick: () => handlePipelineSelect(record),
                    style: {
                      cursor: 'pointer'
                    }
                  })}
                  locale={{
                    emptyText: <ProjectTabTableEmpty />,
                  }}
                  size="middle"
                />
              </div>
            </div>
          )}

          {/* Loading Spinner for DPR Form Loading */}
          {isLoadingDprForm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px'
              }}>
                <Spin size="large" />
                <div style={{
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  Loading DPR
                </div>
              </div>
            </div>
          )}

          {/* Settings Page */}
          {currentStep === 'settings' && (
            <div style={{ padding: '0', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
              {/* Header */}
              <div style={{
                padding: '16px 24px 0',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <h2 style={{
                  margin: 0,
                  color: '#fff',
                  fontSize: '24px',
                  fontWeight: 600
                }}>Settings</h2>
              </div>

              {/* Navigation Tabs - Styled like DPR Page */}
              <div style={{
                padding: '8px 24px',
                fontSize: '16px',
                fontWeight: '500',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '30px',
                margin: '0 24px 24px',
                height: '56px'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '40px',
                  alignItems: 'center',
                  height: '32px'
                }}>
                  {[
                    { label: 'Report Config', key: 'reportConfig' },
                    { label: 'Defect Types', key: 'defectTypes' },
                    { label: 'Team', key: 'team' }
                  ].map((tab) => (
                    activeSettingsTab === tab.key ? (
                      <Button
                        key={tab.key}
                        onClick={() => setActiveSettingsTab(tab.key)}
                        style={{
                          borderRadius: '6px',
                          height: '32px',
                          backgroundColor: '#ff4d4f',
                          borderColor: '#ff4d4f',
                          color: '#fff',
                          fontWeight: 500,
                          fontSize: '16px',
                          padding: '0 16px'
                        }}
                      >
                        {tab.label}
                      </Button>
                    ) : (
                      <span
                        key={tab.key}
                        onClick={() => setActiveSettingsTab(tab.key)}
                        style={{
                          color: 'rgba(255, 255, 255, 0.65)',
                          fontSize: '16px',
                          cursor: 'pointer',
                          fontWeight: 400,
                          userSelect: 'none',
                          padding: '0 4px'
                        }}
                      >
                        {tab.label}
                      </span>
                    )
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              {activeSettingsTab === 'reportConfig' && (
                <div style={{
                  padding: '24px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  height: 'calc(100vh - 300px)',
                  overflowY: 'auto'
                }}>
                  <h3 style={{ color: '#fff', marginBottom: '24px', fontSize: '18px', fontWeight: 600 }}>Report Configuration</h3>

                  {/* Configuration Items */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingBottom: '20px' }}>
                    {/* Summary */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Summary</span>
                      <Switch
                        checked={reportConfig.summary}
                        onChange={(checked) => setReportConfig({ ...reportConfig, summary: checked })}
                        style={{
                          backgroundColor: reportConfig.summary ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Safety */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Safety</span>
                      <Switch
                        checked={reportConfig.safety}
                        onChange={(checked) => setReportConfig({ ...reportConfig, safety: checked })}
                        style={{
                          backgroundColor: reportConfig.safety ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Productivity */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Productivity</span>
                      <Switch
                        checked={reportConfig.productivity}
                        onChange={(checked) => setReportConfig({ ...reportConfig, productivity: checked })}
                        style={{
                          backgroundColor: reportConfig.productivity ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Quality */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Quality</span>
                      <Switch
                        checked={reportConfig.quality}
                        onChange={(checked) => setReportConfig({ ...reportConfig, quality: checked })}
                        style={{
                          backgroundColor: reportConfig.quality ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Personnel */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Personnel</span>
                      <Switch
                        checked={reportConfig.personnel}
                        onChange={(checked) => setReportConfig({ ...reportConfig, personnel: checked })}
                        style={{
                          backgroundColor: reportConfig.personnel ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Comments */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Comments</span>
                      <Switch
                        checked={reportConfig.comments}
                        onChange={(checked) => setReportConfig({ ...reportConfig, comments: checked })}
                        style={{
                          backgroundColor: reportConfig.comments ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Downtime */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Downtime</span>
                      <Switch
                        checked={reportConfig.downtime}
                        onChange={(checked) => setReportConfig({ ...reportConfig, downtime: checked })}
                        style={{
                          backgroundColor: reportConfig.downtime ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Consumables */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Consumables</span>
                      <Switch
                        checked={reportConfig.consumables}
                        onChange={(checked) => setReportConfig({ ...reportConfig, consumables: checked })}
                        style={{
                          backgroundColor: reportConfig.consumables ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Generate Report After Submission */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px 20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>Generate Report After Submission</span>
                      <Switch
                        checked={reportConfig.generateReportAfterSubmission}
                        onChange={(checked) => setReportConfig({ ...reportConfig, generateReportAfterSubmission: checked })}
                        style={{
                          backgroundColor: reportConfig.generateReportAfterSubmission ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div style={{
                    marginTop: '24px',
                    paddingTop: '20px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingBottom: '20px'
                  }}>
                    <Button
                      type="primary"
                      loading={isSavingReportConfig}
                      onClick={handleSaveReportConfig}
                      style={{
                        backgroundColor: '#ff4d4f',
                        borderColor: '#ff4d4f',
                        color: '#fff',
                        borderRadius: '6px',
                        height: '40px',
                        padding: '0 32px',
                        fontSize: '15px',
                        fontWeight: 500
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              )}

              {activeSettingsTab === 'defectTypes' && (
                <div style={{
                  padding: '24px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  height: 'calc(100vh - 300px)',
                  overflowY: 'auto'
                }}>
                  {/* Header Section */}
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <h3 style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: 600 }}>Defect Codes</h3>
                      <Button
                        type="primary"
                        onClick={() => {
                          const newId = Date.now();
                          setDefectCodes([...defectCodes, { id: newId, code: '', description: '', isExisting: false }]);
                        }}
                        style={{
                          backgroundColor: '#ff4d4f',
                          borderColor: '#ff4d4f',
                          color: '#fff',
                          borderRadius: '6px',
                          height: '36px',
                          padding: '0 20px',
                          fontSize: '14px',
                          fontWeight: 500
                        }}
                      >
                        + Add Code
                      </Button>
                    </div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.65)', margin: 0, fontSize: '14px' }}>Configure defect codes for the Weld Log.</p>
                  </div>

                  {/* Defect Codes Card */}
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    {/* Header Row */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '150px 1fr 40px',
                      gap: '16px',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px', fontWeight: 500 }}>Code</span>
                      <span style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px', fontWeight: 500 }}>Description</span>
                      <span></span>
                    </div>

                    {/* Defect Code Rows */}
                    {defectCodes.map((defect, index) => (
                      <div
                        key={defect.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '150px 1fr 40px',
                          gap: '16px',
                          marginBottom: '12px',
                          alignItems: 'center'
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Code"
                          value={defect.code}
                          onChange={(e) => {
                            const updated = defectCodes.map(d =>
                              d.id === defect.id ? { ...d, code: e.target.value } : d
                            );
                            setDefectCodes(updated);
                          }}
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            color: '#fff',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Description"
                          value={defect.description}
                          onChange={(e) => {
                            const updated = defectCodes.map(d =>
                              d.id === defect.id ? { ...d, description: e.target.value } : d
                            );
                            setDefectCodes(updated);
                          }}
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            color: '#fff',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        />
                        <Button
                          type="text"
                          danger
                          onClick={() => {
                            // Remove from local state only - API call happens on Save
                            if (!defect.id) {
                              console.warn('Delete attempted without valid ID');
                              return;
                            }
                            setDefectCodes(prevCodes => {
                              const filtered = prevCodes.filter(d => d.id !== defect.id);
                              if (filtered.length === prevCodes.length) {
                                console.warn(`No defect code found with ID ${defect.id} to delete.`);
                                return prevCodes;
                              }
                              return filtered;
                            });
                          }}
                          style={{
                            color: '#ff4d4f',
                            padding: '4px',
                            height: 'auto',
                            minWidth: 'auto'
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          </svg>
                        </Button>
                      </div>
                    ))}

                    {/* Save Button */}
                    <div style={{
                      marginTop: '24px',
                      paddingTop: '20px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      justifyContent: 'flex-end'
                    }}>
                      <Button
                        type="primary"
                        loading={isSavingReportConfig}
                        onClick={handleSaveWelderConfig}
                        style={{
                          backgroundColor: '#ff4d4f',
                          borderColor: '#ff4d4f',
                          color: '#fff',
                          borderRadius: '6px',
                          height: '40px',
                          padding: '0 32px',
                          fontSize: '15px',
                          fontWeight: 500
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Settings Placeholder */}
              {activeSettingsTab === 'team' && (
                <div style={{
                  padding: '24px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  height: 'calc(100vh - 300px)',
                  overflowY: 'auto'
                }}>
                  <h3 style={{ color: '#fff', marginBottom: '24px', fontSize: '18px', fontWeight: 600 }}>Team Configuration</h3>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.65)',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '200px',
                    border: '1px dashed rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px'
                  }}>
                    Team settings content coming soon.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="inspection-logs p-0 d-flex mb-3 justify-content-between align-items-center tab-content-title">
        {currentStep === 'list' && (
          <Space style={{ marginLeft: 'auto' }}>
            <Button type="primary" onClick={handleAddDpr}>
              Add DPR
            </Button>
            <RangePicker
              className="custom-date-picker"
              allowClear={true}
              format="DD-MMM-YYYY"
              onChange={handleCustomDateSelect}
              value={selectedRange}
            />
          </Space>
        )}
      </div>

      {currentStep !== 'pipelineSelection' && currentStep !== 'settings' && (
        <div style={{
          height: 'calc(100vh - 300px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '0 16px'
        }}>
          {currentStep === 'list' ? (
            <div className='addTeamDetails'>
              <Table
                columns={columns}
                dataSource={dprLogs}
                className="h-100 pipeline-table"
                scroll={{ y: 'calc(100vh - 290px)' }}
                rowKey="id"
                loading={isLoadingDprLogs}
                pagination={{
                  current: Math.floor(dprLogsPagination.offset / dprLogsPagination.limit) + 1,
                  pageSize: dprLogsPagination.limit,
                  total: dprLogsPagination.total,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
                onChange={handleTableChange}
              />
            </div>
          ) : currentStep === 'form' ? (
            <div style={{ padding: '24px', minHeight: '100%' }}>
              {activeCategory === 'Safety' ? (
                <SafetyLog
                  safetyData={safetyData}
                  shift={activeShift}
                  dprLogId={dprLogId}
                  sowId={selectedPipeline?.id}
                  onSave={async (data) => {
                    setIsSavingSafety(true);
                    try {
                      // TODO: Replace with actual API call
                      console.log('Saving safety report:', data);
                      markSectionSaved('savedSafety');
                      // await saveSafetyReport(data);
                      // You can update safetyData here if needed
                      // setSafetyData(updatedData);
                    } catch (error) {
                      console.error('Error saving safety report:', error);
                    } finally {
                      setIsSavingSafety(false);
                    }
                  }}
                  isSubmitting={isSavingSafety}
                />
              ) : activeCategory === 'Quality' ? (
                <QualityLog
                  qualityData={qualityData}
                  shift={activeShift}
                  dprLogId={dprLogId}
                  sowId={selectedPipeline?.id}
                  selectedProject={selectedProject}
                  selectedPipeline={selectedPipeline}
                  onSave={async (data) => {
                    setIsSavingQuality(true);
                    try {
                      // TODO: Replace with actual API call
                      console.log('Saving quality report:', data);
                      markSectionSaved('savedQuality');
                      // await saveQualityReport(data);
                      // You can update qualityData here if needed
                      // setQualityData(updatedData);
                    } catch (error) {
                      console.error('Error saving quality report:', error);
                    } finally {
                      setIsSavingQuality(false);
                    }
                  }}
                  isSubmitting={isSavingQuality}
                />
              ) : activeCategory === 'Productivity' ? (
                <ProductivityLog
                  productivityData={productivityData}
                  shift={activeShift}
                  dprLogId={dprLogId}
                  sowId={selectedPipeline?.id}
                  selectedProject={selectedProject}
                  selectedPipeline={selectedPipeline}
                  refreshToken={productivityTabRefreshToken}
                  onSave={async (data) => {
                    setIsSavingProductivity(true);
                    try {
                      // TODO: Replace with actual API call
                      console.log('Saving productivity report:', data);
                      markSectionSaved('savedProductivity');
                      // await saveProductivityReport(data);
                      // You can update productivityData here if needed
                      // setProductivityData(updatedData);
                    } catch (error) {
                      console.error('Error saving productivity report:', error);
                    } finally {
                      setIsSavingProductivity(false);
                    }
                  }}
                  isSubmitting={isSavingProductivity}
                />
              ) : activeCategory === 'Personnel' ? (
                <PersonnelLog
                  personnelData={personnelData}
                  shift={activeShift}
                  dprLogId={dprLogId}
                  sowId={selectedPipeline?.id}
                  selectedProject={selectedProject}
                  onSave={async (data) => {
                    setIsSavingPersonnel(true);
                    try {
                      // TODO: Replace with actual API call
                      console.log('Saving personnel report:', data);
                      markSectionSaved('savedPersonnel');
                      // await savePersonnelReport(data);
                      // You can update personnelData here if needed
                      // setPersonnelData(updatedData);
                    } catch (error) {
                      console.error('Error saving personnel report:', error);
                    } finally {
                      setIsSavingPersonnel(false);
                    }
                  }}
                  isSubmitting={isSavingPersonnel}
                />
              ) : activeCategory === 'Consumables' ? (
                <ConsumablesLog
                  consumablesData={consumablesData}
                  shift={activeShift}
                  dprLogId={dprLogId}
                  projectId={selectedPipeline?.projectId}
                  sowId={selectedPipeline?.id}
                  onSave={async (data) => {
                    setIsSavingConsumables(true);
                    try {
                      // TODO: Replace with actual API call
                      console.log('Saving consumables report:', data);
                      markSectionSaved('savedConsumables');
                      // await saveConsumablesReport(data);
                      // You can update consumablesData here if needed
                      // setConsumablesData(updatedData);
                    } catch (error) {
                      console.error('Error saving consumables report:', error);
                    } finally {
                      setIsSavingConsumables(false);
                    }
                  }}
                  isSubmitting={isSavingConsumables}
                />
              ) : activeCategory === 'Downtime' ? (
                <DowntimeLog
                  downtimeData={downtimeData}
                  shift={activeShift}
                  dprLogId={dprLogId}
                  sowId={selectedPipeline?.id}
                  onSave={async (data) => {
                    setIsSavingDowntime(true);
                    try {
                      // TODO: Replace with actual API call
                      console.log('Saving downtime report:', data);
                      markSectionSaved('savedDowntime');
                      // await saveDowntimeReport(data);
                      // You can update downtimeData here if needed
                      // setDowntimeData(updatedData);
                    } catch (error) {
                      console.error('Error saving downtime report:', error);
                    } finally {
                      setIsSavingDowntime(false);
                    }
                  }}
                  isSubmitting={isSavingDowntime}
                />
              ) : activeCategory === 'Comments' ? (
                <CommentsLog
                  commentsData={commentsData}
                  shift={activeShift}
                  dprLogId={dprLogId}
                  dprLogData={dprLogData}
                  dprStatus={dprStatus}
                  selectedProject={selectedProject}
                  logDate={selectedDate}
                  skipDayShift={skipped.day}
                  skipNightShift={skipped.night}
                  sowId={selectedPipeline?.id}
                  generateReportAfterSubmission={reportConfig.generateReportAfterSubmission}
                  onSave={async (data) => {
                    setIsSavingComments(true);
                    try {
                      // API call is now handled in CommentsLog component
                      console.log('Comments report saved:', data);
                      if (selectedPipeline?.id) {
                        dispatch(updateDprSavedState(selectedPipeline.id, 'savedComments', true));
                      }
                      const responseData = data?.responseData;
                      if (responseData) {
                        setDprStatus(responseData.status || dprStatus);
                        setCommentsData({
                          dprDayShiftComments: responseData.dprDayShiftComments || '',
                          dprNightShiftComments: responseData.dprNightShiftComments || '',
                          managerName: responseData.approvedBy1?.teamMemberName || '',
                          clientName: responseData.approvedBy2?.teamMemberName || '',
                          managerSignature: responseData.approvedBy1?.signature || responseData.managerSignatureS3Object?.objectUrl || '',
                          clientSignature: responseData.approvedBy2?.signature || responseData.clientSignatureS3Object?.objectUrl || '',
                          managerSignatureTimestamp: responseData.approvedBy1?.signedAt || null,
                          clientSignatureTimestamp: responseData.approvedBy2?.signedAt || null
                        });

                        const isDay = activeShift === 'day';
                        const savedFlags = {
                          savedSafety: Boolean(isDay ? responseData.isSafetySubmittedDayShift : responseData.isSafetySubmittedNightShift),
                          savedQuality: Boolean(isDay ? responseData.isQualitySubmittedDayShift : responseData.isQualitySubmittedNightShift),
                          savedProductivity: Boolean(isDay ? responseData.isProductivitySubmittedDayShift : responseData.isProductivitySubmittedNightShift),
                          savedPersonnel: Boolean(isDay ? responseData.isPersonnelSubmittedDayShift : responseData.isPersonnelSubmittedNightShift),
                          savedConsumables: Boolean(isDay ? responseData.isConsumablesSubmittedDayShift : responseData.isConsumablesSubmittedNightShift),
                          savedDowntime: Boolean(isDay ? responseData.isDowntimeSubmittedDayShift : responseData.isDowntimeSubmittedNightShift)
                        };

                        if (selectedPipeline?.id) {
                          Object.entries(savedFlags).forEach(([key, value]) => {
                            dispatch(updateDprSavedState(selectedPipeline.id, key, value));
                          });
                        }
                      }
                    } catch (error) {
                      console.error('Error saving comments report:', error);
                    } finally {
                      setIsSavingComments(false);
                    }
                  }}
                  onCancel={() => {
                    // Navigate back to pipeline selection page
                    setCurrentStep('pipelineSelection');
                  }}
                  onGenerateReport={async (data) => {
                    try {
                      // TODO: Replace with actual API call for generating report
                      console.log('Generating comments report:', data);
                      // await generateCommentsReport(data);
                    } catch (error) {
                      console.error('Error generating comments report:', error);
                    }
                  }}
                  isSubmitting={isSavingComments}
                />
              ) : activeCategory === 'Dashboard' ? (
                <DashboardLog
                  shift={activeShift}
                  dprLogId={dprLogId}
                  sowId={selectedPipeline?.id}
                  selectedProject={selectedProject}
                />
              ) : (
                <div>
                  <div style={{ color: '#fff', fontSize: '16px', marginBottom: '16px' }}>
                    Active Category: <strong>{activeCategory}</strong>
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px' }}>
                    DPR form content for {activeCategory} category will be displayed here.
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

    </div>
  );
};

export default DPR;
